import { useState, FormEvent } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';

const stats = [
  { value: '12,000+', label: '视频资源', icon: 'ri-video-line' },
  { value: '5,000万+', label: '月活跃用户', icon: 'ri-user-line' },
  { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
  { value: '6', label: '内容分类', icon: 'ri-stack-line' },
];

const milestones = [
  { year: '2020', title: '平台创立', desc: '妖精视频正式上线，初期聚焦电影和电视剧资源聚合' },
  { year: '2021', title: '品类扩展', desc: '新增综艺、动漫、纪录片等品类，内容数量突破5000部' },
  { year: '2022', title: '用户破千万', desc: '月活跃用户突破1000万，成为国内领先的免费视频平台之一' },
  { year: '2023', title: '技术升级', desc: '全面升级CDN加速和4K高清技术，观影体验大幅提升' },
  { year: '2024', title: '生态完善', desc: '与西瓜视频、91视频等平台达成深度合作，内容生态日趋完善' },
  { year: '2025', title: 'AI赋能', desc: '引入AI智能推荐系统，为用户提供个性化内容推荐服务' },
  { year: '2026', title: '全新启航', desc: '平台全面改版升级，以全新面貌服务亿万用户' },
];

const faqs = [
  {
    q: '妖精视频是否需要注册才能观看？',
    a: '不需要。妖精视频是一个完全免费的在线视频平台，所有内容无需注册即可直接观看，无需下载任何软件或插件，即点即播，方便快捷。',
  },
  {
    q: '妖精视频的视频来源是什么？',
    a: '妖精视频聚合了西瓜视频免费、91视频网站等多个主流视频平台的优质资源，通过智能聚合技术为用户提供一站式的观影体验。所有视频内容均来自合作平台的公开资源。',
  },
  {
    q: '为什么有的视频无法播放？',
    a: '视频无法播放可能由以下原因造成：1）网络连接不稳定，建议切换网络后重试；2）源视频链接已失效，我们会尽快更新；3）浏览器缓存问题，建议清除缓存后刷新页面。如问题持续存在，欢迎通过联系我们表单反馈。',
  },
  {
    q: '妖精视频支持哪些设备观看？',
    a: '妖精视频支持全平台观看，包括电脑端（Windows/Mac）、平板和手机浏览器。我们采用响应式设计，会自动适配不同屏幕尺寸，确保在任何设备上都能获得良好的观影体验。',
  },
  {
    q: '视频画质最高支持什么清晰度？',
    a: '妖精视频支持多种清晰度选择，部分热门内容最高可达4K超清画质。您可以在视频播放页面根据自己的网络状况选择合适的清晰度，获得最佳观影体验。',
  },
  {
    q: '如何反馈问题或提出建议？',
    a: '您可以通过本页面的"联系我们"表单提交反馈和建议，也可以发送邮件至 contact@yaojingvideo.com。我们的团队会认真对待每一条反馈，不断优化平台体验。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('phone_alt') as string || '').trim();
    if (hpValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value && typeof value === 'string' && value.trim()) {
          params.append(key, value.trim());
        }
      });

      const resp = await fetch('https://readdy.ai/api/form/d9n17j4it35ii3jdln9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '妖精视频',
    url: 'https://yaojingvideo.com',
    description: '妖精视频免费在线平台，聚合西瓜视频免费、91视频网站等高清资源，免费观看电视剧全集和最新电影，无需下载不缓冲，即点即播畅享观影。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-8888',
      contactType: 'customer service',
      email: 'contact@yaojingvideo.com',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const combinedSchema = { ...orgSchema, ...faqSchema };

  return (
    <>
      <SEO
        title="关于我们 - 妖精视频 | 品牌介绍与联系方式"
        description="妖精视频品牌介绍，了解我们的发展历程、核心优势和联系方式。妖精视频免费在线平台，聚合西瓜视频免费、91视频网站等高清资源，全心全意为用户提供优质观影体验。"
        keywords="妖精视频,关于我们,品牌介绍,联系方式,免费视频平台"
        canonicalUrl="https://yaojingvideo.com/about"
        ogType="website"
        schema={combinedSchema}
      />

      <Navbar />

      <main>
        <div className="bg-gradient-to-b from-background-100 to-background-50">
          <div className="container-site py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="text-center max-w-3xl mx-auto mt-6">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
                关于妖精视频
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                妖精视频是一个致力于为全球华语用户提供<strong>免费在线视频</strong>服务的聚合平台。
                我们整合了西瓜视频免费、91视频网站等平台的<strong>高清电视剧</strong>、
                <strong>电影大全</strong>等优质内容，让用户无需下载、不缓冲即可畅享观影体验。
              </p>
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14">
          <div className="container-site">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {stats.map(stat => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-5 md:p-6 text-center border border-background-300/30">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-500/10 rounded-full">
                    <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100/50">
          <div className="container-site">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500">一路走来，我们不断成长</p>
            </div>

            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/50 md:-translate-x-px"></div>

              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0 md:px-8`}>
                      <div className="bg-background-100 rounded-xl p-5 border border-background-300/30">
                        <span className="text-xs font-bold text-primary-500 bg-primary-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-2 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500">{item.desc}</p>
                      </div>
                    </div>

                    <div className="absolute left-2 md:left-1/2 md:-translate-x-1/2 top-1 w-5 h-5 bg-primary-500 rounded-full border-4 border-background-50 flex-shrink-0"></div>

                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-site">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mb-6">听听用户们怎么说</p>

                <div className="space-y-4">
                  {[
                    { name: '张先生', text: '妖精视频真的太方便了！不用下载任何软件，打开就能看。画质清晰，缓冲也很快，已经完全取代了我手机里的其他视频App。', rating: 5 },
                    { name: '李女士', text: '作为一名剧迷，妖精视频是我的追剧首选。电视剧更新及时，全集都有，再也不用到处找了。推荐给所有追剧的朋友！', rating: 5 },
                    { name: '王同学', text: '动漫资源很全，国漫日漫都有。播放流畅不卡顿，4K画质看动漫体验真的太好了。希望平台越来越好！', rating: 5 },
                    { name: '刘先生', text: '纪录片频道太赞了！BBC的高清纪录片都能免费看，学习的同时还能享受视觉盛宴。已经推荐给身边很多人了。', rating: 4 },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-300/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                          <span className="text-xs font-bold text-background-50 dark:text-foreground-950">
                            {review.name[0]}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-foreground-900">{review.name}</span>
                        <div className="flex items-center gap-0.5 ml-auto">
                          {Array.from({ length: review.rating }).map((_, j) => (
                            <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-500 leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-6">
                  有任何问题或建议？欢迎通过以下方式联系我们，或填写表单，我们会在24小时内回复。
                </p>

                <div className="bg-background-100 rounded-xl p-6 border border-background-300/30 mb-6">
                  <div className="space-y-4 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-500/10 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-foreground-500 text-xs">电子邮箱</p>
                        <p className="text-foreground-900 font-medium">contact@yaojingvideo.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-accent-500/10 rounded-lg flex-shrink-0">
                        <i className="ri-phone-line text-accent-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-foreground-500 text-xs">客服电话</p>
                        <p className="text-foreground-900 font-medium">400-888-8888</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-secondary-500/10 rounded-lg flex-shrink-0">
                        <i className="ri-map-pin-line text-secondary-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-foreground-500 text-xs">公司地址</p>
                        <p className="text-foreground-900 font-medium">北京市朝阳区建国路88号</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-xl p-6 border border-background-300/30"
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="cf-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="cf-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 bg-background-200 border border-background-300/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="cf-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 bg-background-200 border border-background-300/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="cf-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="cf-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 bg-background-200 border border-background-300/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="cf-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2.5 bg-background-200 border border-background-300/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>

                    <div className="hp-field-wrap">
                      <label htmlFor="cf-phone-alt">Phone</label>
                      <input
                        id="cf-phone-alt"
                        name="phone_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formStatus === 'error' && formError && (
                      <div className="text-sm text-red-400 bg-red-400/10 px-4 py-2 rounded-lg">
                        {formError}
                      </div>
                    )}

                    {formStatus === 'success' && (
                      <div className="text-sm text-green-400 bg-green-400/10 px-4 py-2 rounded-lg">
                        提交成功！我们会尽快回复您。
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100/50">
          <div className="container-site max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于妖精视频的常见疑问解答</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl border border-background-300/30">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-site text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              开始观影之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-6">海量免费高清视频，尽在妖精视频</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              返回首页
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}